import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import { StickyNote } from 'lucide-react';
import { huecosDeLaSerie, conCortes } from '../lib/evolucion';
import useTieneHover from '../hooks/useTieneHover';
import {
  ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent,
} from './ui/chart';
import { cn } from '../lib/utils';

/**
 * La evolución de los pilares en el tiempo.
 *
 * ── EL EJE X ES TEMPORAL, NO CATEGÓRICO ────────────────────────────────────
 * Antes era `dataKey="fecha"`, o sea que Recharts repartía los puntos a
 * distancias iguales sin mirar la fecha: dos check-ins separados por una semana y
 * otros dos separados por un mes se dibujaban igual de lejos. Con datos semanales
 * regulares no se nota; en una serie real —donde alguien deja de contestar tres
 * semanas y vuelve— el gráfico dibujaba una recuperación suave donde hubo un
 * hueco.
 *
 * Y es además lo que hace posible marcar las notas: una nota puede caer un día en
 * el que no hubo check-in, y en un eje categórico esa fecha simplemente no existe.
 *
 * ── CUÁNTAS SERIES SE PUEDEN VER JUNTAS ────────────────────────────────────
 * Cinco, no seis, y no es una opinión. Los colores de abajo se pasaron por el
 * validador de paletas comparando TODOS los pares, no sólo los adyacentes:
 *
 *     2 series → ΔE 30.8      4 series → ΔE 16.8
 *     3 series → ΔE 30.3      5 series → ΔE 15.0  (justo en el piso)
 *     6 series → ΔE 11.7  ✗   verde y verde-azulado no se distinguen
 *
 * El piso de 15 es duro: por debajo, alguien con visión de color COMPLETA no
 * separa las dos líneas — no es un problema de daltonismo, es de que no hay
 * suficiente diferencia. Por eso, cuando se piden los seis pilares, la salida no
 * es apretar un sexto color sino FACETAR: seis gráficos chicos, uno por pilar,
 * con el mismo eje. Comparar formas entre paneles es más fácil que desenredar
 * seis líneas cruzadas, y de paso cada serie queda sola y no compite con nadie.
 *
 * ── EL COLOR SIGUE AL PILAR, NUNCA AL ORDEN ────────────────────────────────
 * `COLORES` es un mapa por id, no un array que se recorre. Si el color saliera
 * del orden de selección, destildar un pilar repintaría a los demás y el gráfico
 * de antes no se podría comparar con el de después — que es justamente lo que
 * uno hace al tildar y destildar.
 *
 * ── ESCALAS MEZCLADAS ──────────────────────────────────────────────────────
 * Cada check-in trae su `escala`. Los viejos son 1-5 y los nuevos 1-10, así que
 * todo se normaliza a 1-10 ANTES de dibujar. Sin eso, media serie aparecería
 * aplastada contra el piso y se leería como un derrumbe que no ocurrió.
 */

/** Un color por pilar, fijo. El primero es el de la marca. */
const COLORES = {
  nutricion: '#ff5722',
  actividad: '#9c27b0',
  sueno: '#00897b',
  estres: '#1e88e5',
  sustancias: '#d81b60',
  conexion: '#43a047',
};

/** Arriba de esto, superponer deja de ser legible y se pasa a la grilla. */
const MAX_SUPERPUESTAS = 5;

const ESCALA = 10;

/** Pasa un valor de su escala a 1-10 conservando la posición relativa. */
const normalizar = (valor, escala) => {
  const v = Number(valor);
  if (!Number.isFinite(v)) return null;
  const desde = Number(escala) >= 2 ? Number(escala) : 5;
  if (desde === ESCALA) return v;
  return Math.round((1 + ((v - 1) / (desde - 1)) * (ESCALA - 1)) * 100) / 100;
};

/** "12 ago" — el año no aporta en una serie de semanas. */
const comoDia = (ms) =>
  new Date(ms).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * El contenido del tooltip: los valores del día más los comentarios por pilar.
 *
 * POR QUÉ ACÁ Y NO SÓLO EN LA LISTA: el comentario existe para explicar un
 * número, y el momento en que a alguien le importa es mirando la caída en el
 * gráfico. Tenerlo sólo en la lista obliga a recordar la fecha y ir a buscarlo,
 * que es exactamente la fricción que el módulo tiene que sacar.
 */
function ContenidoDelTooltip({ pilares, ...props }) {
  const fila = props.payload?.[0]?.payload;
  const comentarios = fila?.comentarios || {};
  // Sólo los de las series que se están mirando: mostrar el comentario de un
  // pilar destildado sería contestar una pregunta que nadie hizo.
  const visibles = (props.payload || [])
    .map((s) => [s.dataKey, comentarios[s.dataKey]])
    .filter(([, texto]) => texto);

  return (
    <div>
      <ChartTooltipContent {...props} labelFormatter={comoDia} />
      {visibles.length > 0 && (
        <div className="mt-1 flex flex-col gap-1 rounded-lg border border-neutral-200 bg-white p-2 shadow-md">
          {visibles.map(([id, texto]) => (
            <p key={id} className="m-0 max-w-[16rem] text-[0.72rem] leading-snug text-neutral-600">
              <strong className="text-neutral-900">
                {pilares.find((p) => p.id === id)?.nombre || id}:
              </strong>{' '}
              {texto}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** El instante UTC de una fecha YYYY-MM-DD, para el eje. */
const aInstante = (iso) => {
  const [a, m, d] = String(iso).split('-').map(Number);
  if (!a || !m || !d) return null;
  // Date.UTC y no `new Date(iso)`: a UTC-3 el segundo cae el día anterior a las
  // 21, y el punto se dibujaría un día corrido.
  return Date.UTC(a, m - 1, d);
};

export default function GraficoDeEvolucion({
  checkins = [], pilares = [], notas = [], frecuenciaDias,
}) {
  const ids = pilares.map((p) => p.id);
  const [elegidos, setElegidos] = useState(() => ids.slice(0, 2));
  const [modo, setModo] = useState('superpuesto');
  const [notaAbierta, setNotaAbierta] = useState(null);
  // Qué nota está bajo el mouse, en escritorio. Es distinto de `notaAbierta`:
  // una se abre tocando y se queda, la otra sigue al puntero.
  const [notaEncima, setNotaEncima] = useState(null);
  const tieneHover = useTieneHover();

  const nombreDe = (id) => pilares.find((p) => p.id === id)?.nombre || id;

  const puntos = useMemo(
    () => checkins
      .map((c) => {
        const t = aInstante(c.fecha);
        if (t === null) return null;
        const fila = { t, comentarios: c.comentarios || {} };
        ids.forEach((id) => { fila[id] = normalizar(c.valores?.[id], c.escala); });
        return fila;
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkins, pilares],
  );

  /* Los tramos sin datos. El criterio —dos períodos del propio paciente— vive en
     lib/evolucion.js con sus pruebas, porque se equivoca en silencio: muy
     sensible pica una serie sana, muy laxo devuelve la pendiente falsa. */
  const huecos = useMemo(
    () => huecosDeLaSerie(puntos.map((p) => p.t), frecuenciaDias),
    [puntos, frecuenciaDias],
  );

  // Con la fila vacía en el medio de cada hueco, `connectNulls={false}` corta la
  // línea sola.
  const datos = useMemo(() => conCortes(puntos, huecos, ids),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [puntos, huecos, pilares]);

  /* Las notas que caen dentro del rango dibujado. Una nota anterior al primer
     check-in o posterior al último no tiene dónde ponerse: el eje no llega, y
     estirarlo para alcanzarla comprimiría todos los datos por una nota suelta. */
  const marcas = useMemo(() => {
    if (puntos.length === 0) return [];
    const desde = puntos[0].t;
    const hasta = puntos[puntos.length - 1].t;
    return notas
      .map((n) => ({ ...n, t: aInstante(n.fecha) }))
      .filter((n) => n.t !== null && n.t >= desde && n.t <= hasta);
  }, [notas, puntos]);

  const config = useMemo(() => {
    const c = {};
    elegidos.forEach((id) => { c[id] = { label: nombreDe(id), color: COLORES[id] }; });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elegidos, pilares]);

  const alternar = (id) => setElegidos((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  if (puntos.length === 0) {
    return (
      <p className="m-0 py-10 text-center text-sm text-neutral-500">
        Todavía no hay check-ins cargados. En cuanto haya dos, acá se ve la evolución.
      </p>
    );
  }

  const enGrilla = modo === 'grilla';
  const demasiadas = !enGrilla && elegidos.length > MAX_SUPERPUESTAS;
  const dominio = [puntos[0].t, puntos[puntos.length - 1].t];

  /** Los ejes y la grilla, iguales en el gráfico grande y en cada panel chico. */
  const ejes = (compacto) => (
    <>
      <CartesianGrid vertical={false} stroke="var(--gray-200, #eee)" />
      <XAxis
        dataKey="t"
        type="number"
        domain={dominio}
        scale="time"
        tickFormatter={comoDia}
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        fontSize={compacto ? 10 : 12}
        // Sin esto Recharts inventa marcas en fechas donde no hay nada.
        ticks={puntos.map((d) => d.t)}
        interval="preserveStartEnd"
      />
      <YAxis
        domain={[1, ESCALA]}
        ticks={compacto ? [1, 5, 10] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
        tickLine={false}
        axisLine={false}
        tickMargin={6}
        fontSize={compacto ? 10 : 12}
        width={compacto ? 24 : 32}
      />
    </>
  );

  /**
   * Las bandas de "sin datos".
   *
   * Van JUNTO con el corte de la línea, no en vez de él. Sólo cortar deja al
   * lector preguntándose si falta un dato o si el gráfico se rompió; sólo la
   * banda, sin cortar, sigue mostrando la pendiente falsa por debajo.
   *
   * Y es UN elemento por hueco, no uno por serie: con seis pilares tildados no
   * se multiplica, que es lo que pasaría marcando el hueco con una línea
   * punteada por cada serie.
   */
  const bandasDeHueco = huecos.map((h) => (
    <ReferenceArea
      key={`hueco-${h.desde}`}
      x1={h.desde}
      x2={h.hasta}
      fill="var(--gray-300, #e0e0e0)"
      fillOpacity={0.35}
      strokeOpacity={0}
      label={{
        value: `sin datos · ${h.dias}d`,
        position: 'insideTop',
        fontSize: 10,
        fill: 'var(--gray-600, #757575)',
      }}
    />
  ));

  /**
   * Las marcas de nota.
   *
   * Con hover, la etiqueta es un cuadradito sobre la línea que reacciona al
   * mouse. Sin hover no se le pone nada: en un teléfono, un objetivo de 10px es
   * imposible de acertar con el dedo y encima taparía el gráfico. Ahí las notas
   * se abren desde los botones de abajo.
   */
  const lineasDeNota = marcas.map((n) => (
    <ReferenceLine
      key={n.id}
      x={n.t}
      stroke={notaEncima === n.id || notaAbierta === n.id
        ? 'var(--gray-700, #616161)'
        : 'var(--gray-400, #9e9e9e)'}
      strokeDasharray="3 3"
      strokeWidth={1}
      label={tieneHover ? (props) => {
        const { viewBox } = props;
        if (!viewBox) return null;
        return (
          <g
            transform={`translate(${viewBox.x - 6}, ${viewBox.y - 2})`}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setNotaEncima(n.id)}
            onMouseLeave={() => setNotaEncima(null)}
          >
            {/* El rectángulo transparente es el área sensible: la marca visible
                mide 12px y sola sería un blanco difícil incluso con mouse. */}
            <rect x={-6} y={-4} width={24} height={22} fill="transparent" />
            <rect
              width={12}
              height={12}
              rx={2}
              fill={notaEncima === n.id ? 'var(--gray-700, #616161)' : 'var(--gray-400, #9e9e9e)'}
            />
            <path d="M3 4 h6 M3 6.5 h6 M3 9 h4" stroke="white" strokeWidth={1} />
          </g>
        );
      } : undefined}
    />
  ));

  const serie = (id, extra = {}) => (
    <Line
      key={id}
      type="monotone"
      dataKey={id}
      stroke={COLORES[id]}
      strokeWidth={2}
      dot={{ r: 3 }}
      activeDot={{ r: 6 }}
      // `false` y no `true`: un pilar sin contestar es un hueco, y unir por
      // encima dibujaría una continuidad que no existe.
      connectNulls={false}
      {...extra}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      {/* ── Qué pilares mirar ──────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5">
        {pilares.map((p) => {
          const activo = elegidos.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => alternar(p.id)}
              aria-pressed={activo}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.78rem] font-medium transition-colors',
                activo
                  ? 'border-neutral-300 bg-white text-neutral-900 shadow-sm'
                  : 'border-transparent bg-neutral-100 text-neutral-500 hover:bg-neutral-200',
              )}
            >
              {/* El punto lleva el color de SU serie, así que la relación entre
                  el chip y la línea no depende de recordar el orden. */}
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: activo ? COLORES[p.id] : 'transparent',
                         boxShadow: activo ? 'none' : `inset 0 0 0 1px ${COLORES[p.id]}` }}
                aria-hidden="true"
              />
              {p.nombre}
            </button>
          );
        })}

        <span className="ml-auto inline-flex overflow-hidden rounded-full border border-neutral-300">
          {[['superpuesto', 'Juntos'], ['grilla', 'En grilla']].map(([id, texto]) => (
            <button
              key={id}
              type="button"
              onClick={() => setModo(id)}
              aria-pressed={modo === id}
              className={cn(
                'px-2.5 py-1 text-[0.75rem] font-medium transition-colors',
                modo === id ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50',
              )}
            >
              {texto}
            </button>
          ))}
        </span>
      </div>

      {/* El aviso dice por qué, y NO cambia de modo solo: que la pantalla decida
          por uno es peor que el problema que resuelve. */}
      {demasiadas && (
        <p className="m-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[0.78rem] text-amber-900">
          Con más de {MAX_SUPERPUESTAS} series los colores dejan de distinguirse entre sí.
          Probá <strong>En grilla</strong>.
        </p>
      )}

      {elegidos.length === 0 ? (
        <p className="m-0 py-10 text-center text-sm text-neutral-500">
          Elegí al menos un pilar para ver su evolución.
        </p>
      ) : enGrilla ? (
        /* ── Grilla: un panel por pilar, mismo eje ─────────────────────── */
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {elegidos.map((id) => (
            <div key={id} className="rounded-lg border border-neutral-200 p-2">
              <p className="m-0 mb-1 flex items-center gap-1.5 px-1 text-[0.78rem] font-semibold text-neutral-700">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLORES[id] }} aria-hidden="true" />
                {nombreDe(id)}
              </p>
              <ChartContainer
                config={{ [id]: { label: nombreDe(id), color: COLORES[id] } }}
                className="aspect-auto h-[140px] w-full"
              >
                <ResponsiveContainer>
                  <LineChart data={datos} margin={{ top: 4, right: 8, bottom: 0, left: -24 }}>
                    {ejes(true)}
                    {bandasDeHueco}
                    {lineasDeNota}
                    <ChartTooltip cursor content={<ChartTooltipContent labelFormatter={comoDia} />} />
                    {serie(id, { dot: { r: 2 } })}
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          ))}
        </div>
      ) : (
        /* ── Superpuesto ───────────────────────────────────────────────── */
        <ChartContainer config={config} className="aspect-auto h-[300px] w-full">
          <ResponsiveContainer>
            <LineChart data={datos} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
              {ejes(false)}
              {bandasDeHueco}
              {lineasDeNota}
              <ChartTooltip cursor content={<ContenidoDelTooltip pilares={pilares} />} />
              {/* La leyenda va siempre que haya dos o más: sin ella la identidad
                  de cada línea sería sólo el color. */}
              {elegidos.length > 1 && <ChartLegend content={<ChartLegendContent />} />}
              {elegidos.map((id) => serie(id))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}

      {/* ── Las notas ─────────────────────────────────────────────────────
          Botones debajo del gráfico, alineados con sus líneas punteadas.

          POR QUÉ NO SÓLO HOVER SOBRE LA LÍNEA: en un teléfono el hover no
          existe, y una marca de 1px de ancho es imposible de acertar con el
          dedo. Estos botones son el objetivo táctil —44px de alto— y además
          funcionan con teclado, que una ReferenceLine no. */}
      {marcas.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="m-0 flex items-center gap-1.5 text-[0.72rem] text-neutral-500">
            <StickyNote className="h-3 w-3" aria-hidden="true" />
            Notas de sesión ({marcas.length}) —{' '}
            {tieneHover
              ? 'pasá el mouse por las marcas del gráfico'
              : 'tocá una fecha para leerla'}
          </p>

          {/* Los botones son para el caso SIN hover. Con mouse sobran: la marca
              del gráfico ya hace el trabajo y una fila de fechas repetida
              debajo sólo agrega ruido.

              Igual quedan accesibles por teclado en los dos casos — ver abajo. */}
          {!tieneHover && (
            <div className="flex flex-wrap gap-1.5">
              {marcas.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setNotaAbierta(notaAbierta === n.id ? null : n.id)}
                  aria-expanded={notaAbierta === n.id}
                  className={cn(
                    'min-h-[2.25rem] rounded-full border px-3 py-1 text-[0.75rem] tabular-nums transition-colors',
                    notaAbierta === n.id
                      ? 'border-neutral-900 bg-neutral-900 text-white'
                      : 'border-neutral-300 bg-white text-neutral-600',
                  )}
                >
                  {comoDia(n.t)}
                </button>
              ))}
            </div>
          )}

          {/* Con hover, las fechas siguen existiendo pero sólo para el teclado:
              una marca SVG con `onMouseEnter` no se alcanza tabulando, y sin
              esto las notas quedarían fuera del alcance de quien no usa mouse. */}
          {tieneHover && (
            <div className="flex flex-wrap gap-1.5">
              {marcas.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onFocus={() => setNotaEncima(n.id)}
                  onBlur={() => setNotaEncima(null)}
                  onMouseEnter={() => setNotaEncima(n.id)}
                  onMouseLeave={() => setNotaEncima(null)}
                  className="sr-only focus:not-sr-only focus:rounded-full focus:border focus:border-neutral-900 focus:px-3 focus:py-1 focus:text-[0.75rem]"
                >
                  Nota del {comoDia(n.t)}
                </button>
              ))}
            </div>
          )}

          {(notaEncima || notaAbierta) && (
            <p className="m-0 whitespace-pre-wrap rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 text-[0.82rem] text-neutral-700">
              <span className="mr-1.5 font-semibold tabular-nums text-neutral-500">
                {comoDia(marcas.find((n) => n.id === (notaEncima || notaAbierta))?.t)}
              </span>
              {marcas.find((n) => n.id === (notaEncima || notaAbierta))?.texto}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
